import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  isWellFormedToken,
  submitNewPassword,
  validateToken,
  type ValidateReason,
} from '../api/client'
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  checkPassword,
  isPasswordValid,
} from '../lib/passwordPolicy'
import '../styles/ResetPage.css'

type Screen =
  | { kind: 'loading' }
  | { kind: 'form' }
  | { kind: 'invalid'; reason: ValidateReason }
  | { kind: 'success' }
  | { kind: 'network_error' }

/** Generic, enumeration-safe copy for each invalid reason. */
const INVALID_COPY: Record<ValidateReason, { title: string; message: string }> = {
  expired: {
    title: 'Link expired',
    message: 'This reset link has expired. Request a new one from the app.',
  },
  used: {
    title: 'Link already used',
    message: 'This reset link was already used. Request a new one from the app.',
  },
  invalid: {
    title: 'Invalid link',
    message: 'This reset link is invalid. Request a new one from the app.',
  },
}

export default function ResetPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [screen, setScreen] = useState<Screen>({ kind: 'loading' })

  // Form state
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const runValidation = useCallback(async () => {
    if (!isWellFormedToken(token)) {
      setScreen({ kind: 'invalid', reason: 'invalid' })
      return
    }
    setScreen({ kind: 'loading' })
    try {
      const result = await validateToken(token)
      if (result.valid) {
        setScreen({ kind: 'form' })
      } else {
        setScreen({ kind: 'invalid', reason: result.reason ?? 'invalid' })
      }
    } catch {
      setScreen({ kind: 'network_error' })
    }
  }, [token])

  useEffect(() => {
    void runValidation()
  }, [runValidation])

  const checks = useMemo(() => checkPassword(password), [password])
  const policyOk = useMemo(() => isPasswordValid(password), [password])
  const matches = password.length > 0 && password === confirm
  const canSubmit = policyOk && matches && !submitting

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setFormError(null)

      if (!matches) {
        setFormError('Passwords do not match.')
        return
      }
      if (!policyOk) {
        setFormError('Please meet all password requirements.')
        return
      }

      setSubmitting(true)
      try {
        const outcome = await submitNewPassword(token, password)
        switch (outcome.status) {
          case 'success':
            setScreen({ kind: 'success' })
            break
          case 'weak_password':
            setFormError(
              outcome.message ??
                'That password does not meet the requirements. Please choose another.',
            )
            break
          case 'invalid_token':
            setScreen({ kind: 'invalid', reason: 'invalid' })
            break
          case 'rate_limited':
            setFormError('Too many attempts, please try again later.')
            break
          case 'network_error':
            setFormError('Network error. Please check your connection and try again.')
            break
          case 'server_error':
          default:
            setFormError('Something went wrong on our end. Please try again.')
            break
        }
      } finally {
        setSubmitting(false)
      }
    },
    [matches, policyOk, token, password],
  )

  return (
    <div className="page">
      <div className="card">
        <header className="card-header">
          <div className="brand">Daikin eQuip</div>
          <h1 className="card-title">Reset your password</h1>
        </header>

        {screen.kind === 'loading' && <LoadingView />}

        {screen.kind === 'network_error' && (
          <ErrorView
            title="Connection problem"
            message="We couldn't reach the server. Please check your connection and try again."
            actionLabel="Retry"
            onAction={() => void runValidation()}
          />
        )}

        {screen.kind === 'invalid' && (
          <ErrorView
            title={INVALID_COPY[screen.reason].title}
            message={INVALID_COPY[screen.reason].message}
          />
        )}

        {screen.kind === 'success' && (
          <div className="state-block" role="status">
            <div className="state-icon state-icon--success" aria-hidden="true">
              &#10003;
            </div>
            <h2 className="state-title">Password updated</h2>
            <p className="state-message">
              Your password has been updated. Return to the Daikin eQuip app and log in
              with your new password.
            </p>
          </div>
        )}

        {screen.kind === 'form' && (
          <form className="form" onSubmit={handleSubmit} noValidate>
            <p className="form-intro">Choose a new password for your account.</p>

            <label className="field-label" htmlFor="new-password">
              New password
            </label>
            <div className="input-row">
              <input
                id="new-password"
                className="text-input"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                maxLength={PASSWORD_MAX_LENGTH}
                aria-describedby="policy-hints"
              />
              <button
                type="button"
                className="toggle-btn"
                onClick={() => setShowPassword((s) => !s)}
                aria-pressed={showPassword}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>

            <label className="field-label" htmlFor="confirm-password">
              Confirm password
            </label>
            <div className="input-row">
              <input
                id="confirm-password"
                className="text-input"
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                maxLength={PASSWORD_MAX_LENGTH}
              />
            </div>

            {confirm.length > 0 && !matches && (
              <p className="inline-warn">Passwords do not match.</p>
            )}

            <ul id="policy-hints" className="policy-hints" aria-live="polite">
              <PolicyHint ok={checks.length}>
                {PASSWORD_MIN_LENGTH}&ndash;{PASSWORD_MAX_LENGTH} characters
              </PolicyHint>
              <PolicyHint ok={checks.letter}>At least one letter</PolicyHint>
              <PolicyHint ok={checks.digit}>At least one number</PolicyHint>
              <PolicyHint ok={checks.notBlank}>Not only spaces</PolicyHint>
            </ul>

            {formError && (
              <p className="form-error" role="alert">
                {formError}
              </p>
            )}

            <button type="submit" className="submit-btn" disabled={!canSubmit}>
              {submitting ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}
      </div>

      <p className="footnote">Daikin eQuip account security</p>
    </div>
  )
}

function LoadingView() {
  return (
    <div className="state-block" role="status" aria-live="polite">
      <div className="spinner" aria-hidden="true" />
      <p className="state-message">Checking your reset link&hellip;</p>
    </div>
  )
}

function ErrorView({
  title,
  message,
  actionLabel,
  onAction,
}: {
  title: string
  message: string
  actionLabel?: string
  onAction?: () => void
}) {
  return (
    <div className="state-block" role="alert">
      <div className="state-icon state-icon--warn" aria-hidden="true">
        !
      </div>
      <h2 className="state-title">{title}</h2>
      <p className="state-message">{message}</p>
      {actionLabel && onAction && (
        <button type="button" className="submit-btn" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </div>
  )
}

function PolicyHint({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li className={ok ? 'policy-hint policy-hint--ok' : 'policy-hint'}>
      <span className="policy-mark" aria-hidden="true">
        {ok ? '✓' : '○'}
      </span>
      <span>{children}</span>
    </li>
  )
}
